import React, { useState } from "react";
import { CheckCircle2, FileSignature, Loader2 } from "lucide-react";
import { toast } from "sonner";
import SignatureCapture from "../shared/SignatureCapture";
import { useSignMasterBol } from "../../data/truckingData";
import { getApiErrorMessage } from "../../api/client";

/**
 * The operator's signature on a move job's master BOL (api#941, 0470).
 *
 * POST /trucking/move-jobs/:id/master-bol/sign needs `signedBy` and EXACTLY ONE
 * of:
 * - `signature_key`: a confirmed upload filed against THIS master BOL
 *   (`entityType: 'master_bol'`, `entityId: <master BOL id>`). SignatureCapture
 *   does presign → PUT → confirm and hands back the key only after all three.
 * - `signed_on_paper: true`: the consultant signed a paper copy. Never blocked.
 *
 * A sign with only `signedBy` is a 400 now, so Sign stays disabled until one of
 * the two is chosen. The API's refusal (SIGNATURE_NOT_FOUND, _NOT_CONFIRMED,
 * "must be complete", …) is shown as it is sent.
 *
 * Used by the office's Move Job detail and by a driver on that move job
 * (DriverMasterBolScreen). The API decides who may upload: office roles, and a
 * driver named on one of the move job's loads; anyone else's upload is a 404,
 * which SignatureCapture shows.
 */
export interface MasterBolForSign {
  id?: string | null;
  status?: string | null;
  operator_signed_by?: string | null;
  operator_signature_key?: string | null;
  operator_signed_on_paper?: boolean | null;
}

type Method = "drawn" | "paper";

export function MasterBolSignedNote({ masterBol }: { masterBol: MasterBolForSign }) {
  const how = masterBol.operator_signed_on_paper
    ? "on paper"
    : masterBol.operator_signature_key
      ? "drawn signature"
      : null;
  return (
    <p data-testid="master-bol-signed" className="flex items-center gap-2 text-sm font-medium text-fieldiq-text">
      <CheckCircle2 className="size-4 shrink-0" aria-hidden="true" />
      <span>
        Signed{masterBol.operator_signed_by ? ` by ${masterBol.operator_signed_by}` : ""}
        {how ? ` · ${how}` : ""}
      </span>
    </p>
  );
}

export default function MasterBolSignPanel({
  moveJobId,
  masterBol,
  onSigned,
}: {
  moveJobId: string;
  masterBol: MasterBolForSign;
  onSigned?: () => void;
}) {
  const sign = useSignMasterBol();
  const [signedBy, setSignedBy] = useState("");
  const [method, setMethod] = useState<Method | null>(null);
  const [signatureKey, setSignatureKey] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);

  const masterBolId = masterBol.id ?? null;
  const ready = signedBy.trim() !== "" && (method === "paper" || (method === "drawn" && signatureKey !== null));

  const submit = async () => {
    if (!ready) return;
    setRefusal(null);
    const payload =
      method === "paper"
        ? { signedBy: signedBy.trim(), signed_on_paper: true }
        : { signedBy: signedBy.trim(), signature_key: signatureKey };
    try {
      await sign.mutateAsync({ moveJobId, payload });
      toast.success("Master BOL signed");
      onSigned?.();
    } catch (err) {
      setRefusal(getApiErrorMessage(err, "The master BOL was not signed."));
    }
  };

  const choice = (value: Method, label: string) => (
    <label className="flex items-center gap-2 text-sm text-fieldiq-text">
      <input
        type="radio"
        name="master-bol-sign-method"
        value={value}
        checked={method === value}
        onChange={() => {
          setMethod(value);
          setRefusal(null);
        }}
        disabled={value === "drawn" && !masterBolId}
      />
      {label}
    </label>
  );

  return (
    <div data-testid="master-bol-sign-panel" className="space-y-4">
      <div>
        <label htmlFor="master-bol-signed-by" className="text-xs text-fieldiq-muted">
          Signed by (operator)
        </label>
        <input
          id="master-bol-signed-by"
          className="mt-1 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 py-2 text-sm text-fieldiq-text"
          value={signedBy}
          onChange={(e) => setSignedBy(e.target.value)}
          placeholder="Full name of signing operator"
          autoComplete="name"
        />
      </div>

      <fieldset className="space-y-2">
        <legend className="text-xs text-fieldiq-muted">Signature</legend>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          {choice("drawn", "Draw signature")}
          {choice("paper", "Signed on paper")}
        </div>
      </fieldset>

      {method === "drawn" && masterBolId && (
        <SignatureCapture
          formName="master-bol"
          submissionId={masterBolId}
          entityType="master_bol"
          label="Operator signature"
          onSigned={(key) => setSignatureKey(key)}
          onCleared={() => setSignatureKey(null)}
        />
      )}

      {refusal && (
        <p role="alert" data-testid="master-bol-sign-refusal" className="text-sm font-medium text-fieldiq-text">
          {refusal}
        </p>
      )}

      <button
        type="button"
        onClick={submit}
        disabled={!ready || sign.isPending}
        className="fi-action fi-action--standard fi-action--primary disabled:opacity-50"
      >
        {sign.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <FileSignature className="size-4" aria-hidden="true" />}
        Sign Master BOL
      </button>
    </div>
  );
}
